import { useState, useEffect } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../context/AuthContext'
import { notifyClubAdmins } from '../../../lib/notificationHelpers'

const CLUB_ID = 'a0000000-0000-0000-0000-000000000001'

export default function DonationForm({ campaign, onClose }) {
  const { user } = useAuth()
  const [amount, setAmount] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [isPaid, setIsPaid] = useState(false)
  const [checkingStatus, setCheckingStatus] = useState(true)

  useEffect(() => {
    const checkStatus = async () => {
      if (!user) { setCheckingStatus(false); return }
      const { data } = await supabase
        .from('subscriptions').select('valid_until')
        .eq('supporter_id', user.id).order('valid_until', { ascending: false }).limit(1).single()
      if (data && new Date(data.valid_until) >= new Date()) setIsPaid(true)
      setCheckingStatus(false)
    }
    checkStatus()
  }, [user])

  const handleDonate = async () => {
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      setError('Please enter a valid amount')
      return
    }
    setLoading(true)
    setError(null)

    const { error: donationError } = await supabase
      .from('welfare_donations').insert({ campaign_id: campaign.id, donor_id: user?.id, amount: Number(amount) })
    if (donationError) { setError(donationError.message); setLoading(false); return }

    const { error: updateError } = await supabase
      .from('welfare_campaigns').update({ raised_amount: campaign.raised_amount + Number(amount) }).eq('id', campaign.id)
    if (updateError) { setError(updateError.message); setLoading(false); return }

    try {
      await notifyClubAdmins({
        clubId: CLUB_ID,
        title: 'New Donation Received',
        message: `A supporter donated ZMW ${Number(amount).toLocaleString()} to "${campaign.title}"`,
        type: 'donation',
        link: '/admin/welfare',
      })
    } catch (err) { console.error('Notification failed:', err) }

    setSuccess(true)
    setLoading(false)
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4">
      <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-white/10 p-6 sm:p-8 w-full max-w-md">

        {success ? (
          <div className="text-center py-6 sm:py-8">
            <div className="text-green-600 dark:text-green-400 font-black text-xl sm:text-2xl uppercase tracking-tight mb-3">
              Thank You!
            </div>
            <p className="text-gray-500 text-sm mb-6">
              Your donation of ZMW {Number(amount).toLocaleString()} has been recorded.
            </p>
            <button onClick={onClose} className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 font-bold uppercase tracking-widest text-sm transition-colors">
              Close
            </button>
          </div>

        ) : !checkingStatus && !isPaid ? (
          /* Blocked — not a paid member */
          <div className="text-center py-6">
            <div className="flex justify-end mb-2">
              <button onClick={onClose} className="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-xl">✕</button>
            </div>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-yellow-500 mx-auto mb-4">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <div className="font-black text-lg uppercase tracking-tight mb-2 text-gray-900 dark:text-white">
              Members Only
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              Only active paid members can donate to welfare campaigns. Pay your subscription to unlock this feature.
            </p>
            <button onClick={onClose} className="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-3 font-bold uppercase tracking-widest text-sm transition-colors">
              Got It
            </button>
          </div>

        ) : (
          <>
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1 mr-4">
                <div className="text-xs text-gray-500 uppercase tracking-widest mb-2">Donating to</div>
                <h3 className="font-bold text-gray-900 dark:text-white text-base leading-snug">{campaign.title}</h3>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-900 dark:hover:text-white text-xl transition-colors shrink-0">✕</button>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm mb-4">{error}</div>
            )}

            <div className="flex flex-col gap-2 mb-6">
              <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Donation Amount (ZMW)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-600 w-full"
              />
            </div>

            <div className="grid grid-cols-4 gap-2 mb-6">
              {[50, 100, 200, 500].map((preset) => (
                <button
                  key={preset}
                  onClick={() => setAmount(String(preset))}
                  className="border border-gray-200 dark:border-white/10 hover:border-green-600 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-white py-2 text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  {preset}
                </button>
              ))}
            </div>

            <button
              onClick={handleDonate}
              disabled={loading || checkingStatus}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-900 disabled:text-green-700 text-white py-4 font-bold uppercase tracking-widest text-sm transition-colors"
            >
              {loading ? 'Processing...' : 'Confirm Donation'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}